import {
  formatAmount,
  formatAmountNumber,
  formatExactAmount,
  formatNominalValue,
  formatPrice,
} from "@/utils/currency";
import {
  DateFormatOptions,
  DateTimeFormatOptions,
  formatDate,
  formatDateAndTime,
  formatDateTime,
  formatTime,
  formatWeekday,
  formatYear,
  formatYearMonth,
  TimeFormatOptions,
} from "./dateFormat";
import {
  formatNumber,
  FormatNumberOptions,
  formatPercent,
  FormatPercentOptions,
} from "@/utils/numbers";
import { compact, memoize } from "lodash";
import { Language } from "./languages";
import { DateValue } from "@/utils/Dates";
import { getCountryName } from "@/utils/countries";
import { Amount } from "@/common/common";
import { findShareTypeName, ShareType, ShareTypeFormatOptions } from "@/common/share";
import { formatOrgNum } from "@/utils/other";

export type ListFormatOptions = {
  listType?: "conjunction" | "disjunction" | "unit";
  style?: "long" | "short" | "narrow";
  separator?: string;
};

export type LocaleFormat = {
  formatAmount: (currencyOrAmount: string | Amount, input?: number) => string;
  formatAmountNumber: (input: number | undefined) => string;
  formatNominalValue: (currency: string, input?: number) => string;
  formatDateTime: (input: DateValue | undefined, options?: DateFormatOptions) => string;
  formatDate: (input: DateValue | undefined, options?: DateFormatOptions) => string;
  formatExactAmount: (amount: Amount) => string;
  formatTime: (input: DateValue | undefined, options?: TimeFormatOptions) => string;
  formatDateAndTime: (
    dateInput: DateValue | null | undefined,
    timeInput:
      | DateValue
      | null
      | undefined
      | { startTime: DateValue | null | undefined; endTime: DateValue | null | undefined },
    options?: DateTimeFormatOptions,
  ) => string;
  formatWeekday: (input: DateValue | undefined) => string;
  formatNumber: (input?: number, options?: FormatNumberOptions) => string;
  formatPrice: (currency: string, input?: number) => string;
  formatPercent: (input: number | undefined, options?: FormatPercentOptions) => string;
  formatYearMonth: (input: DateValue | null | undefined) => string;
  formatYear: (input: DateValue | null | undefined) => string;
  formatList: (input: (string | number)[], options?: ListFormatOptions) => string;
  formatCountry: (code: string | undefined) => string;
  formatShareType: (shareType: ShareType, options?: ShareTypeFormatOptions) => string;
  formatSsn: (ssn: string, options?: { nationality?: string }) => string;
  formatOrgNo: (orgNo: string | undefined) => string;
};

const makeListFormat = memoize(
  (lang: Language, options?: ListFormatOptions) => {
    if (options?.separator !== undefined) {
      return { format: (items: Iterable<string>) => Array.from(items).join(options?.separator) };
    }
    return new Intl.ListFormat(lang, { type: options?.listType, style: options?.style });
  },
  (lang, options) =>
    compact([lang, options?.listType, options?.style, options?.separator]).join("-"),
);

export const createLocaleFormat = memoize((lang: Language): LocaleFormat => {
  return {
    formatAmount: (currencyOrAmount, input) => formatAmount(lang, currencyOrAmount, input),
    formatAmountNumber: (input) => formatAmountNumber(input, lang),
    formatNominalValue: (currency, input) => formatNominalValue(lang, currency, input),
    formatDateTime: (input, options) => formatDateTime(lang, input, options),
    formatDate: (input, options) => formatDate(lang, input, options),
    formatExactAmount: (amount: Amount) => formatExactAmount(lang, amount),
    formatTime: (input, options) => formatTime(lang, input, options),
    formatDateAndTime: (dateInput, timeInput, options) =>
      formatDateAndTime(lang, dateInput, timeInput, options),
    formatWeekday: (input) => formatWeekday(lang, input),
    formatNumber: (input, options) => formatNumber(lang, input, options),
    formatPrice: (currency, input) => formatPrice(lang, currency, input),
    formatPercent: (input, options) => formatPercent(lang, input, options),
    formatYearMonth: (input) => formatYearMonth(lang, input),
    formatYear: formatYear,
    formatList: (input, options) =>
      makeListFormat(lang, options).format(input.map((x) => String(x))),
    formatCountry: (code) => getCountryName(code?.toUpperCase(), lang) ?? "",
    formatShareType: (shareType, options) => findShareTypeName(shareType, lang, options),
    formatSsn: (ssn, options) => {
      if (!ssn) {
        return "";
      }
      switch (options?.nationality?.toLowerCase()) {
        case "no":
          return ssn.substring(0, 6) + " ...";
        default:
          if (ssn.length >= 9) {
            return ssn.substring(0, 6) + " ...";
          }
          return "...";
      }
    },
    formatOrgNo: formatOrgNum,
  };
});
